/**
 * Sentinel segment meaning "the organization's default workspace".
 *
 * The value `"default"` is never a real workspace ID (IDs look like
 * `ws-123` or UUIDs), so an id-lookup against the workspace list will never
 * match it, and the route resolution always falls back to the active DEFAULT
 * workspace. It is used to build URLs before the workspace list is loaded
 * (org home paths, post-login redirects).
 */
export const DEFAULT_WORKSPACE_ID = "default"
export const WORKSPACE_REQUEST_PATH_HEADER = "x-coval-workspace-path"
const WORKSPACE_PATH_SEGMENT = "workspace"

export const ORGANIZATION_ROUTE_ROOTS: ReadonlySet<string> = new Set([
  "admin",
  "agents",
  "alerts",
  "benchmarks",
  "conversations",
  "dashboard",
  "home",
  "launch-eval",
  "metrics",
  "monitoring",
  "onboarding",
  "personas",
  "reports",
  "review",
  "runs",
  "scheduled",
  "settings",
  "simulate",
  "simulations",
  "simulated",
  "templates",
  "test-sets",
  "traces",
  "uploaded",
])

/**
 * First path segments that are never an organization slug.
 *
 * `ORGANIZATION_ROUTE_ROOTS` above describes the second segment, the route root
 * under an organization. This describes the first, and the two sets are not
 * interchangeable: `settings` appears in both, as `/settings` and as
 * `/{org}/settings`.
 *
 * These are the static top-level pages. Next.js resolves a static route ahead of
 * its sibling `[slug]` dynamic route, so `/billing` always serves the billing
 * page and an organization slugged `billing` is already unreachable there.
 * Reading one as an organization slug makes the reconciler look up a membership
 * that cannot exist, warn, and, when an organization is active, navigate the
 * user off the page. `/shared/reports/{id}` is a public report link that parses
 * as the organization "shared" because `reports` is a valid organization route
 * root, which is how a shared link redirects a signed-in viewer into their own
 * organization.
 *
 * `benchmarks` and `plans` have no top-level page and are kept only because they
 * were already treated as non-organization roots; dropping them would newly
 * expose `/benchmarks` to that same misreading.
 *
 * `workspaceRoutes.test.ts` derives the expectation from `src/pages` and asserts
 * every static top-level page appears here, so adding a page without updating
 * this set fails there rather than silently turning the route into an
 * organization slug.
 */
export const NON_ORGANIZATION_ROUTE_ROOTS: ReadonlySet<string> = new Set([
  "admin",
  "agent-training",
  "api",
  "benchmarks",
  "billing",
  "getting-started",
  "home",
  "integrations",
  "marketplace",
  "plans",
  "reactivate",
  "select-org",
  "settings",
  "shared",
  "signin",
  "signup",
  "signup-welcome",
  "sso-callback",
  "tts-benchmarks",
])

/**
 * Route roots that are scoped to an organization by default. Individual
 * settings sections may opt back into workspace scope below.
 */
const ORGANIZATION_GLOBAL_ROUTE_ROOTS: ReadonlySet<string> = new Set([
  "settings",
])

/**
 * Settings sections whose state belongs to a workspace rather than the whole
 * organization. These retain the canonical workspace segment even though the
 * rest of Settings is organization-global.
 */
const WORKSPACE_SETTINGS_PATH_PREFIXES: readonly string[] = [
  "workspaces/metrics",
  "workspaces/tags",
]

/**
 * The segment in a canonical URL (`/{org}/workspace/<segment>/...`) is the
 * workspace ID (`WorkspaceResource.id`). It is matched opaquely against the
 * workspace list; a slug, display name, or any other attribute never matches.
 *
 * It was called `workspaceSlug` historically to keep the type shape stable
 * across the codebase; it now holds the workspace ID.
 */
interface WorkspaceRoute {
  organizationSlug: string
  workspaceId: string
  relativePath: string
}

function stripQueryHash(path: string): string {
  const qIndex = path.indexOf("?")
  const hIndex = path.indexOf("#")
  if (qIndex === -1 && hIndex === -1) {
    return path
  }
  const end =
    qIndex === -1 ? hIndex : hIndex === -1 ? qIndex : Math.min(qIndex, hIndex)
  return path.slice(0, end)
}

function splitPath(path: string): string[] | null {
  const segments = path.split("/")
  let start = 0
  if (segments[0] === "") {
    start = 1
  }
  let end = segments.length
  if (end > 0 && segments[end - 1] === "") {
    end--
  }
  if (start >= end) {
    return []
  }
  for (let i = start; i < end; i++) {
    if (segments[i] === "") {
      return null
    }
  }
  return segments.slice(start, end)
}

/**
 * Whether a segment is an un-interpolated Next dynamic route parameter.
 *
 * Before hydration completes, `router.asPath` on a dynamic route is the route
 * pattern itself, so the first segment reads as the literal `[slug]`. That is
 * never an organization, and treating it as one sends the reconciler looking
 * for a membership that cannot exist and then rewriting the URL around it,
 * carrying the other un-interpolated segments along.
 */
function isDynamicRouteSegment(segment: string): boolean {
  return segment.startsWith("[") && segment.endsWith("]")
}

/**
 * Parse a canonical workspace URL and extract its segments.
 *
 * The workspace segment is the workspace ID (`WorkspaceResource.id`), not the
 * slug. It is treated opaquely here — no format validation is performed.
 */
export function parseWorkspaceRoute(pathOrUrl: string): WorkspaceRoute | null {
  const path = stripQueryHash(pathOrUrl)
  const segments = splitPath(path)

  if (!segments) {
    return null
  }

  if (segments.length < 2) {
    return null
  }

  const orgSlug = segments[0]
  if (!orgSlug || isDynamicRouteSegment(orgSlug)) {
    return null
  }

  if (segments[1] !== WORKSPACE_PATH_SEGMENT) {
    return null
  }

  const workspaceId = segments[2]
  if (!workspaceId) {
    return null
  }

  const remaining = segments.slice(3)
  let relativePath = `/${remaining.join("/")}`
  if (relativePath === "/") {
    relativePath = "/home"
  }

  return { organizationSlug: orgSlug, workspaceId, relativePath }
}

export function parseLegacyOrganizationRoute(
  pathOrUrl: string,
): { organizationSlug: string; relativePath: string } | null {
  const path = stripQueryHash(pathOrUrl)
  const segments = splitPath(path)

  if (!segments) {
    return null
  }

  if (segments.length < 1) {
    return null
  }

  const orgSlug = segments[0]
  if (!orgSlug || isDynamicRouteSegment(orgSlug)) {
    return null
  }

  if (segments.length >= 2 && segments[1] === WORKSPACE_PATH_SEGMENT) {
    return null
  }

  if (segments.length === 1) {
    return null
  }

  const relativePath = `/${segments.slice(1).join("/")}`

  if (!ORGANIZATION_ROUTE_ROOTS.has(segments[1])) {
    return null
  }

  return { organizationSlug: orgSlug, relativePath }
}

/** Returns the organization named by a canonical or legacy application URL. */
export function getRouteOrganizationSlug(pathOrUrl: string): string | null {
  const slug =
    parseWorkspaceRoute(pathOrUrl)?.organizationSlug ??
    parseLegacyOrganizationRoute(pathOrUrl)?.organizationSlug ??
    null

  // A legacy route matches on its second segment, so a global route whose
  // second segment happens to be an organization route root reads its first
  // segment as an organization: `/shared/reports/{id}` would resolve to the
  // organization "shared". Callers use this to decide whether a route is
  // organization-scoped at all, so the first segment has to be excluded here
  // rather than left to each of them.
  if (slug !== null && NON_ORGANIZATION_ROUTE_ROOTS.has(slug)) {
    return null
  }

  return slug
}

/**
 * Whether an organization-relative path (e.g. `/settings/api-keys`) belongs to
 * a route root that must stay outside workspace URLs.
 */
export function isOrganizationGlobalPath(relativePath: string): boolean {
  const segments = splitPath(stripQueryHash(relativePath))
  const root = segments?.[0]
  if (!root || !ORGANIZATION_GLOBAL_ROUTE_ROOTS.has(root)) {
    return false
  }

  const settingsPath = segments?.slice(1).join("/")
  return !(
    root === "settings" &&
    settingsPath &&
    WORKSPACE_SETTINGS_PATH_PREFIXES.some(
      (path) => settingsPath === path || settingsPath.startsWith(`${path}/`),
    )
  )
}

/**
 * Extract the workspace ID segment from a canonical URL.
 *
 * The returned segment is the workspace ID (`WorkspaceResource.id`), not the
 * slug.
 */
export function getRouteWorkspaceId(pathOrUrl: string): string | null {
  const parsed = parseWorkspaceRoute(pathOrUrl)
  return parsed ? parsed.workspaceId : null
}

/**
 * Build a canonical workspace path.
 *
 * `workspaceId` is the workspace ID (`WorkspaceResource.id`), not the slug.
 */
export function buildWorkspacePath(
  organizationSlug: string,
  workspaceId: string,
  path = "/home",
): string {
  if (!organizationSlug?.trim() || !workspaceId?.trim()) {
    throw new Error("organizationSlug and workspaceId must be non-blank")
  }

  let suffix = ""
  const qIndex = path.indexOf("?")
  const hIndex = path.indexOf("#")
  if (qIndex !== -1 || hIndex !== -1) {
    const end =
      qIndex === -1 ? hIndex : hIndex === -1 ? qIndex : Math.min(qIndex, hIndex)
    suffix = path.slice(end)
    path = path.slice(0, end)
  }

  path = path.replace(/^\/\[slug\]/, "")

  if (!path || path === "/") {
    path = "/home"
  }

  if (!path.startsWith("/")) {
    path = `/${path}`
  }

  path = path.replace(/\/+/g, "/")

  return `/${organizationSlug}/${WORKSPACE_PATH_SEGMENT}/${workspaceId}${path}${suffix}`
}

/**
 * Builds the organization home path using the sentinel workspace segment.
 *
 * The sentinel `"default"` is never a real workspace ID: it is a transient
 * placeholder for the organization's active DEFAULT workspace.
 * `WorkspaceProviderRoute` normalizes the URL to a real workspace ID, so the
 * sentinel never settles in the URL bar. On this home path it prefers the last
 * workspace the user opened in the organization.
 */
export function buildOrganizationHomePath(organizationSlug: string): string {
  return buildWorkspacePath(organizationSlug, DEFAULT_WORKSPACE_ID)
}

export function getInternalOrganizationPath(route: WorkspaceRoute): string {
  return `/${route.organizationSlug}${route.relativePath}`
}

type WorkspaceRouteQuery = Readonly<
  Record<string, string | string[] | undefined>
>

type WorkspaceAwareHref =
  | string
  | {
      pathname: string
      query: Record<string, string | string[] | undefined>
      hash?: string
    }

interface WorkspaceAwareNavigation {
  href: WorkspaceAwareHref
  as?: string
}

function replaceRouteQuery(
  url: URL,
  query: WorkspaceRouteQuery,
  dynamicKeys: ReadonlySet<string>,
): string {
  url.search = ""
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || dynamicKeys.has(key)) {
      continue
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        url.searchParams.append(key, item)
      }
    } else {
      url.searchParams.set(key, value)
    }
  }

  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * Builds internal and visible Pages Router targets for query-only navigation.
 *
 * The internal `href` always names the route pattern (`/[slug]/...`) with the
 * dynamic params in `query`. A pre-resolved internal path (`/acme/...`) makes
 * the client router adopt that concrete path as `router.pathname` after a
 * shallow navigation, so later writes no longer recognise `slug` as a route
 * param and leak it into the visible query string (`?slug=acme&...`).
 */
export function buildWorkspaceAwareNavigation(
  asPath: string | undefined,
  routePattern: string,
  query: WorkspaceRouteQuery,
): WorkspaceAwareNavigation {
  const definedQuery = Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined),
  )
  const workspaceRoute = asPath ? parseWorkspaceRoute(asPath) : null
  if (!asPath || !workspaceRoute) {
    return { href: { pathname: routePattern, query: definedQuery } }
  }

  const dynamicKeys = new Set<string>()
  for (const match of routePattern.matchAll(/\[\[?(?:\.{3})?([^\]]+)\]\]?/g)) {
    dynamicKeys.add(match[1])
  }

  const visibleUrl = new URL(asPath, "https://coval.local")

  return {
    href: {
      pathname: routePattern,
      query: definedQuery,
      ...(visibleUrl.hash ? { hash: visibleUrl.hash } : {}),
    },
    as: replaceRouteQuery(visibleUrl, query, dynamicKeys),
  }
}
